import React, {
  ChangeEvent,
  FormEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { atom, useAtom } from 'jotai';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import {
  Text,
  Select,
  TextField,
  Button,
  ButtonGroup,
  IconButton,
  Icon,
  Modal,
  Card,
  Tooltip,
  Skeleton,
  MultiValue,
  Spinner,
} from '@nike/eds';
import Input from 'components/Input';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUser,
  deleteUser,
  identityFetch,
  updateUser,
} from '../../clientAPI/identity';
import { QUERY_KEYS } from '../../constants';
import { useRoleList } from './hooks';
import { isExistScope } from '../../utils/authHelpers';

const EMAIL_REGEXP = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

type SelectOption = {
  label: string;
  value: string;
};

type UserListApiResponse = {
  users: User[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
  count: number;
};

// state

const editingUserAtom = atom<User | null>(null);

const deletingUserAtom = atom<User | null>(null);

const listUserQueryAtom = atom<{
  pageSize?: string;
  currentPage?: string;
  email?: string;
}>({ pageSize: '10' });

// hooks

function useUserList() {
  const revalidator = useRevalidator();
  const [listUserQuery] = useAtom(listUserQueryAtom);
  const qs = new URLSearchParams(removeNullishValue(listUserQuery)).toString();
  return useQuery([QUERY_KEYS.USER_LIST, qs], identityFetch(), {
    staleTime: Infinity,
    suspense: true,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });
}

function useUpdateUserMutation(oldUser: User, onSuccess: () => void) {
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();
  return useMutation(
    ({ roles }: { roles: string[] }) => {
      return updateUser(oldUser._id, { roles });
    },
    {
      onSuccess: (update: { roles: string[] }) => {
        queryClient.setQueriesData(
          [QUERY_KEYS.USER_LIST],
          (prev: UserListApiResponse | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              users: prev.users.map((user) => {
                return user._id === oldUser._id ? { ...user, ...update } : user;
              }),
            };
          },
        );
        onSuccess();
      },
      onError: (err: { status: number }) =>
        err.status === 401 && revalidator.revalidate(),
    },
  );
}

function useCreateUserMutation(onSuccess: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (data: Partial<Omit<User, '_id'>>) => {
      return createUser(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.USER_LIST]);
        onSuccess();
      },
    },
  );
}

function useDeleteUserMutation(onSuccess: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (userId: string) => {
      return deleteUser(userId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.USER_LIST]);
        onSuccess();
      },
    },
  );
}

// components

export function UserList() {
  return (
    <UserListShell>
      <UserListCore />
    </UserListShell>
  );
}

function UserListCore() {
  const { data } = useUserList();
  if (data.users.length === 0) {
    return (
      <div className='tw-flex tw-justify-center tw-py-10 tw-text-center'>
        No Users Found
      </div>
    );
  }
  return (
    <ul className='tw-list-none'>
      {data.users.map((u: User) => {
        return (
          <li key={u._id} className='tw-py-2'>
            <UserItem user={u} />
          </li>
        );
      })}
    </ul>
  );
}

function UserListPagination() {
  const { data } = useUserList();
  const page = data.currentPage;
  const hasMoreData = data.totalPages > page;
  const noPagination = page === 1 && !hasMoreData;
  const [, setListUserQuery] = useAtom(listUserQueryAtom);
  const setPage = useCallback(
    (page: number) => {
      const currentPage = '' + page;
      setListUserQuery((prev) => {
        return { ...prev, currentPage };
      });
    },
    [setListUserQuery],
  );
  return (
    <div className='tw-flex tw-items-center tw-justify-between tw-pb-40'>
      {!noPagination && (
        <p className='tw-text-sm tw-font-bold'>Current Page: {page}</p>
      )}
      <ButtonGroup>
        {page !== 1 && (
          <Button size='small' onClick={() => setPage(page - 1)}>
            Previous Page
          </Button>
        )}
        {hasMoreData && (
          <Button size='small' onClick={() => setPage(page + 1)}>
            Next Page
          </Button>
        )}
      </ButtonGroup>
    </div>
  );
}

function UserListSkeleton() {
  return (
    <ul className='tw-list-none'>
      {new Array(10).fill(1, 0).map((_u: string, idx: number) => {
        return (
          <li key={idx} className='tw-py-2'>
            <Skeleton height={76} />
          </li>
        );
      })}
    </ul>
  );
}

function UserListShell({ children }: { children: React.ReactNode }) {
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const emailValueRef = useRef<string>('');
  const [, setListUserQuery] = useAtom(listUserQueryAtom);
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:identity:write', jwt);
  const onSearch = () => {
    const email = emailInputRef?.current?.value.trim() || '';
    emailValueRef.current = email;
    setListUserQuery(() => {
      return { email };
    });
  };
  useEffect(() => {
    // reset email query if user navigated away from this page
    return () => {
      if (emailValueRef.current !== '') {
        setListUserQuery((query) => ({ ...query, email: '' }));
      }
    };
  }, [setListUserQuery]);
  const [editingUser, setEditingUser] = useAtom(editingUserAtom);
  const [deletingUser, setDeletingUser] = useAtom(deletingUserAtom);
  return (
    <div className='tw-max-w-5xl tw-mx-auto tw-p-10'>
      <p className='eds-type--display-5 tw-mb-4'>User List</p>
      <div className='tw-flex tw-justify-between tw-my-6 tw-items-center'>
        <div className='tw-flex'>
          <Input
            placeholder='Email'
            className='tw-border-b tw-w-44 tw-mr-4'
            ref={emailInputRef}
            onSave={onSearch}
          />
          <Tooltip bodySlot='Search' enableFocus>
            <IconButton
              icon='Search'
              size='small'
              variant='primary'
              disableRipple={true}
              onClick={onSearch}
              label='Search'
            />
          </Tooltip>
        </div>
        <div>
          {canWrite && (
            <Tooltip bodySlot='Create User' enableFocus>
              <IconButton
                icon='Plus'
                size='small'
                disableRipple={true}
                onClick={() => setIsCreatingUser(true)}
                label='Edit'
              />
            </Tooltip>
          )}
        </div>
      </div>
      <Suspense fallback={<UserListSkeleton />}>{children}</Suspense>
      <Suspense fallback={null}>
        <UserListPagination />
      </Suspense>
      <UserEditModal
        user={editingUser}
        isCreate={isCreatingUser}
        onDismiss={() => {
          if (editingUser) setEditingUser(null);
          if (isCreatingUser) setIsCreatingUser(false);
        }}
      />
      <UserDeleteModal
        user={deletingUser}
        onDismiss={() => {
          setDeletingUser(null);
        }}
      />
    </div>
  );
}

function UserItem(props: { user: User }) {
  const { user } = props;
  const [, setEditingUser] = useAtom(editingUserAtom);
  const [, setDeletingUser] = useAtom(deletingUserAtom);
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:identity:write', jwt);
  return (
    <Card padding={16}>
      <div className='tw-flex tw-justify-between tw-items-center'>
        <div>
          <div>{user.name}</div>
          <div className='tw-text-gray-500 tw-text-sm'>{user.email}</div>
        </div>
        <div className='tw-flex tw-gap-3'>
          <ul className='tw-flex tw-items-center tw-gap-1 tw-flex-wrap tw-pl-3'>
            {user.roles.map((role) => {
              return (
                <li
                  key={role}
                  className='tw-inline-flex tw-items-center tw-justify-center tw-text-xs tw-rounded-full tw-bg-orange-300 tw-text-orange-800 tw-py-1 tw-px-2'
                >
                  {role}
                </li>
              );
            })}
          </ul>
          <div className='tw-flex tw-gap-1'>
            {canWrite && (
              <Tooltip bodySlot='Edit User' enableFocus>
                <IconButton
                  icon={<Icon name='Edit' size='s' />}
                  variant='secondary'
                  size='small'
                  disableRipple={true}
                  onClick={() => setEditingUser(user)}
                  label='Edit'
                />
              </Tooltip>
            )}
            {canWrite && (
              <Tooltip bodySlot='Delete User' enableFocus>
                <IconButton
                  icon={<Icon name='Delete' size='s' />}
                  variant='secondary'
                  style={{ color: 'var(--eds-color-red-50)' }}
                  size='small'
                  disableRipple={true}
                  onClick={() => setDeletingUser(user)}
                  label='Delete'
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function UserFormButtonGroup(props: {
  onDismiss: () => void;
  onSubmit: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  submitButtonText: string;
}) {
  return (
    <ButtonGroup className='tw-justify-end'>
      {props.isLoading ? (
        <LoadingButton size='medium'>{props.submitButtonText}</LoadingButton>
      ) : (
        <Button disableRipple onClick={props.onSubmit} size='medium'>
          {props.submitButtonText}
        </Button>
      )}
      <Button
        disableRipple
        onClick={props.onDismiss}
        size='medium'
        variant='secondary'
      >
        Cancel
      </Button>
    </ButtonGroup>
  );
}

function UserEditForm(props: {
  user: User;
  onDismiss: () => void;
  onSubmitSucces: () => void;
}) {
  const { data } = useRoleList();
  const allRoles: SelectOption[] = (data?.roles || []).map((role: Role) => ({
    label: role.name,
    value: role._id,
  }));
  const roleMap = new Map<string, SelectOption>(
    allRoles.map((role) => [role.label, role]),
  );

  const initialAssignedRoles = (props.user?.roles || []).map(
    (roleName: string) => {
      // skip undefined check since roleMap must contains this role
      return roleMap.get(roleName) as SelectOption;
    },
  );

  const [selectedRoles, setSelectedRoles] = useState(initialAssignedRoles);

  const [rolesError, setRolesError] = useState('');

  const onRolesChange = useCallback(
    (v: MultiValue<SelectOption>) => {
      setSelectedRoles(v as SelectOption[]);
      if (rolesError) setRolesError('');
    },
    [rolesError],
  );

  const mutation = useUpdateUserMutation(props.user, props.onSubmitSucces);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();

    const roles = selectedRoles.map((r) => r.label);
    if (roles.length === 0) {
      setRolesError('Roles can not be empty');
      return;
    }

    mutation.mutate({ roles });
  };

  return (
    <form
      id='user-edit-form'
      className='tw-flex tw-flex-col tw-gap-2'
      style={{ minWidth: '500px', maxWidth: '75vw' }}
    >
      <div>
        <Select
          id='roles'
          label='Roles'
          isMulti
          value={selectedRoles}
          onChange={onRolesChange}
          options={allRoles}
          menuPosition='fixed'
          menuShouldScrollIntoView={false}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          menuPortalTarget={
            document.querySelector('#user-edit-form') as HTMLElement
          }
          hasErrors={!!rolesError}
          errorMessage={rolesError}
        />
      </div>
      <div className='tw-pt-6 tw-pb-0'>
        <UserFormButtonGroup
          isLoading={mutation.isLoading}
          onSubmit={onSubmit}
          onDismiss={props.onDismiss}
          submitButtonText='Save'
        />
      </div>
    </form>
  );
}

function UserCreateForm(props: {
  onSubmitSucces: () => void;
  onDismiss: () => void;
}) {
  const { data } = useRoleList();
  const allRoles: SelectOption[] = (data?.roles || []).map((role: Role) => ({
    label: role.name,
    value: role._id,
  }));

  const [selectedRoles, setSelectedRoles] = useState<SelectOption[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [rolesError, setRolesError] = useState('');

  const onNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
      if (nameError) setNameError('');
    },
    [nameError],
  );
  const onEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      if (emailError) setEmailError('');
    },
    [emailError],
  );
  const onRolesChange = useCallback(
    (v: MultiValue<SelectOption>) => {
      setSelectedRoles(v as SelectOption[]);
      if (rolesError) setRolesError('');
    },
    [rolesError],
  );
  const mutation = useCreateUserMutation(props.onSubmitSucces);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();

    const validatedName = name.trim();
    if (validatedName === '') {
      setNameError('Name can not be empty');
      return;
    }
    const validatedEmail = email.trim();
    if (validatedEmail === '') {
      setEmailError('Email can not be empty');
      return;
    }
    if (!EMAIL_REGEXP.test(validatedEmail)) {
      setEmailError('Invalid email address');
      return;
    }
    const roles = selectedRoles.map((r) => r.label);
    if (roles.length === 0) {
      setRolesError('Roles can not be empty');
      return;
    }

    mutation.mutate({ roles, name: validatedName, email: validatedEmail });
  };

  return (
    <form
      id='user-create-form'
      className='tw-flex tw-flex-col tw-gap-2'
      style={{ minWidth: '500px', maxWidth: '75vw' }}
    >
      <>
        <div>
          <TextField
            id='name'
            label='Name'
            placeholder='John Doe'
            value={name}
            onChange={onNameChange}
            hasErrors={!!nameError}
            errorMessage={nameError}
          />
        </div>
        <div>
          <TextField
            id='email'
            label='Email'
            placeholder='john.doe@nike.com'
            value={email}
            onChange={onEmailChange}
            hasErrors={!!emailError}
            errorMessage={emailError}
          />
        </div>
      </>
      <div>
        <Select
          id='roles'
          label='Roles'
          isMulti
          value={selectedRoles}
          onChange={onRolesChange}
          options={allRoles}
          menuPosition='fixed'
          menuShouldScrollIntoView={false}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          menuPortalTarget={
            document.querySelector('#user-create-form') as HTMLElement
          }
          hasErrors={!!rolesError}
          errorMessage={rolesError}
        />
      </div>
      <div className='tw-pt-6 tw-pb-0'>
        <UserFormButtonGroup
          isLoading={mutation.isLoading}
          onSubmit={onSubmit}
          onDismiss={props.onDismiss}
          submitButtonText='Create'
        />
      </div>
    </form>
  );
}

function UserEditModal(props: {
  user: User | null;
  onDismiss: () => void;
  isCreate: boolean;
}) {
  if (!props.isCreate && !props.user) return null;

  return (
    <Modal
      className='user-edit-modal'
      onDismiss={props.onDismiss}
      headerSlot={props.isCreate ? 'Create User' : props.user?.email || 'Edit'}
      isOpen={!!props.user || props.isCreate}
    >
      <div className='tw-flex tw-flex-col tw-justify-center'>
        <Suspense
          fallback={
            <div className='tw-flex tw-items-center tw-justify-center tw-p-4'>
              <Spinner />
            </div>
          }
        >
          {props.isCreate ? (
            <UserCreateForm
              onDismiss={props.onDismiss}
              onSubmitSucces={props.onDismiss}
            />
          ) : props.user ? (
            <UserEditForm
              user={props.user}
              onDismiss={props.onDismiss}
              onSubmitSucces={props.onDismiss}
            />
          ) : null}
        </Suspense>
      </div>
    </Modal>
  );
}

function UserDeleteModal(props: { user: User | null; onDismiss: () => void }) {
  const mutation = useDeleteUserMutation(props.onDismiss);
  const onSubmit = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    mutation.mutate(props.user?._id || '');
  };
  if (!props.user) return null;
  return (
    <Modal
      headerSlot={'Delete User'}
      isOpen={!!props.user}
      onDismiss={props.onDismiss}
    >
      <Text>Are you sure you want to delete this user?</Text>
      <Text className='tw-pt-2'>{props.user.email}</Text>
      <div className='tw-pt-8'>
        <UserFormButtonGroup
          isLoading={mutation.isLoading}
          onDismiss={props.onDismiss}
          onSubmit={onSubmit}
          submitButtonText='Confirm'
        />
      </div>
    </Modal>
  );
}

function LoadingButton(props: Parameters<typeof Button>[0]) {
  return (
    <Button
      beforeSlot={
        <span className='eds--dark'>
          <Spinner />
        </span>
      }
      disableRipple
      {...props}
    />
  );
}

function removeNullishValue(o: Record<string, string | null | undefined>) {
  const copy = { ...o };
  for (const key in copy) {
    if (copy[key] === '' || copy[key] === null || copy[key] === undefined) {
      delete copy[key];
    }
  }
  return copy as Record<string, string>;
}
